import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import { SuccessToastContainer } from '../../components/Toast/ToastContainer';
import { IAction, ICalculatorRules, ICreateUpdateCalculatorRules } from '../../types';
import * as Actions from './actions';
import { CalculatorRules } from './types';

interface ICalculatorRulesPayload {
  siteId: string;
}

interface ICalculatorRulesUpdatePayload extends ICalculatorRulesPayload {
  body: ICreateUpdateCalculatorRules;
  onSuccess?: () => void;
  successMessage?: string;
}

interface IGenerateCalculatorRulesPayload extends ICalculatorRulesPayload {
  onSuccess: (rules: ICreateUpdateCalculatorRules) => void;
  onError: (error: any) => void;
}

export function* getCalculatorRules({ payload }: IAction<CalculatorRules, ICalculatorRulesPayload>) {
  try {
    const response: ApiResponse<ICalculatorRules> = yield call(API.Sites.listCalculatorRules, payload.siteId);

    yield put(Actions.getCalculatorRulesSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getCalculatorRulesError(error));
  }
}

export function* updateCalculatorRules({ payload }: IAction<CalculatorRules, ICalculatorRulesUpdatePayload>) {
  try {
    const response: ApiResponse<ICalculatorRules> = yield call(API.Sites.createUpdateCalculatorRules, payload.siteId, payload.body);

    yield put(Actions.updateCalculatorRulesSuccess(response.body));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess);

    // Show success toast
    yield call(toast, SuccessToastContainer({ message: payload.successMessage || 'Calculation rules saved' }));
  } catch (error: any) {
    yield put(Actions.updateCalculatorRulesError(error, true));
  }
}

export function* generateCalculatorRules({ payload }: IAction<CalculatorRules, IGenerateCalculatorRulesPayload>) {
  try {
    const generatedRulesResponse: ApiResponse<ICreateUpdateCalculatorRules> = yield call(API.Sites.generateCalculatorRules, payload.siteId);

    yield put(Actions.generateCalculatorRulesSuccess(generatedRulesResponse.body));

    // Execute on success function
    yield call(payload.onSuccess, generatedRulesResponse.body);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield call(payload.onError, formattedError);
    yield put(Actions.generateCalculatorRulesError(formattedError, false));
  }
}

export function* watchGetCalculatorRules() {
  yield takeEvery(CalculatorRules.GET_CALCULATOR_RULES_REQUEST, getCalculatorRules);
}

export function* watchUpdateCalculatorRules() {
  yield takeEvery(CalculatorRules.UPDATE_CALCULATOR_RULES_REQUEST, updateCalculatorRules);
}

export function* watchGenerateCalculatorRules() {
  yield takeEvery(CalculatorRules.GENERATE_CALCULATOR_RULES_REQUEST, generateCalculatorRules);
}

function* calculatorRulesSaga() {
  yield all([fork(watchGetCalculatorRules), fork(watchUpdateCalculatorRules), fork(watchGenerateCalculatorRules)]);
}
export { calculatorRulesSaga };
